/**
 * Context Injector
 * 
 * Single Responsibility: Handles context injection into AI conversation
 * Follows Dependency Inversion - depends on abstractions
 */

import { InterviewStateManager } from './InterviewStateManager';
import { ConversationMemory } from '../memory/ConversationMemory';

export interface SessionSender {
  send?: (message: { parts: Array<{ text: string }>; turnComplete: boolean }) => void;
}

export class ContextInjector {
  private lastContextInjection: number = 0;
  private readonly contextInjectionInterval: number = 60000; // 60 seconds

  constructor(
    private stateManager: InterviewStateManager,
    private memory: ConversationMemory,
    private sessionPromise: Promise<any> | null
  ) { void 0 }

  /**
   * Inject context update into the conversation
   */
  public injectContext(contextMessage: string, reason: string = 'context-update'): void {
    this.sessionPromise?.then((session) => {
      if (session && typeof (session as any).send === 'function') {
        try {
          (session as any).send({
            parts: [{ text: `SYSTEM_CONTEXT_UPDATE (${reason}): ${contextMessage}` }],
            turnComplete: true,
          });

          this.lastContextInjection = Date.now();

          if (import.meta.env.DEV) { void 0 }
        } catch (err) { void err }
      }
    }).catch(() => { void 0 });
  }

  /**
   * Inject interview state update
   */
  public injectStateUpdate(reason: string = 'state-update'): void {
    const state = this.stateManager.getState();
    const currentQuestion = this.stateManager.getCurrentQuestion();
    const nextQuestion = this.stateManager.getNextQuestion();

    // Prevent injection if all questions are answered
    if (this.stateManager.areAllQuestionsAnswered()) {
      if (import.meta.env.DEV) { void 0 }
      return;
    }

    // Prevent injection if no questions available
    if (!currentQuestion && !nextQuestion) {
      if (import.meta.env.DEV) { void 0 }
      return;
    }

    const stateMessage = this.buildStateUpdateMessage(state, currentQuestion, nextQuestion);
    this.injectContext(stateMessage, reason);
  }

  /**
   * Build state update message for context injection
   */
  private buildStateUpdateMessage(
    state: any,
    currentQuestion: any,
    nextQuestion: any
  ): string {
    const parts: string[] = [];

    parts.push(`CURRENT INTERVIEW STATE:`);
    parts.push(`- You are on question ${state.currentQuestionIndex + 1} of ${state.totalQuestions}`);

    if (state.answeredQuestions.length > 0) {
      parts.push(`- Questions already answered: ${state.answeredQuestions.length}`);
    }

    if (nextQuestion) {
      parts.push(`- Next question to ask: "${nextQuestion.summary || nextQuestion.prompt.substring(0, 100)}..."`);
      parts.push(`- Question prompt: ${nextQuestion.prompt}`);
      parts.push(`\nContinue with the interview. Ask the next question if you haven't already.`);
    } else if (this.stateManager.areAllQuestionsAnswered()) {
      parts.push(`- All questions have been asked and answered. Proceed to closing phase.`);
      parts.push(`\nContinue with the interview closing. Do not ask any more questions.`);
    } else {
      parts.push(`- Continue with the current question.`);
    }

    // Add recent conversation summary
    const recentSummary = this.memory.getRecentSummary(3);
    if (recentSummary && recentSummary !== 'No recent conversation history.') {
      parts.push(`\nRECENT CONVERSATION:`);
      parts.push(recentSummary);
    }

    return parts.join('\n');
  }

  /**
   * Inject recovery context when stuck
   */
  public injectRecoveryContext(): void {
    const nextQuestion = this.stateManager.getNextQuestion();
    if (!nextQuestion) {
      return;
    }

    const state = this.stateManager.getState();
    const recoveryMessage = `RECOVERY: You may have lost track of the interview progress. Here's where you are:

CURRENT POSITION: Question ${state.currentQuestionIndex + 1} of ${state.totalQuestions}

NEXT QUESTION TO ASK:
"${nextQuestion.prompt}"

Please ask this question now and wait for the candidate's response.`;

    this.injectContext(recoveryMessage, 'recovery-stuck-detection');
  }

  /**
   * Check if periodic context injection is needed
   */
  public shouldInjectPeriodicContext(): boolean {
    const now = Date.now();
    const timeSinceLastInjection = now - this.lastContextInjection;

    return (
      timeSinceLastInjection >= this.contextInjectionInterval &&
      this.stateManager.isPastIntroduction() &&
      !this.stateManager.areAllQuestionsAnswered()
    );
  }

  /**
   * Update session promise (needed when session changes)
   */
  public updateSession(sessionPromise: Promise<any> | null): void {
    this.sessionPromise = sessionPromise;
  }
}

